<script setup>
import { computed, ref } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t, locale } = useI18n();
const { updateUISettings } = useUISettings();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const toggleChatSupport = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

/* 语言收在一项里，点开再选。语言名不走 i18n，始终用该语言自己的名字。 */
const languageItems = [
  { code: 'zh_CN', label: '中文' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh_TW', label: '繁體中文' },
  { code: 'en', label: 'English' },
  { code: 'th', label: 'ไทย' },
];
const languageGroupLabels = {
  zh_CN: '语言',
  zh_TW: '語言',
  ja: '言語',
  ko: '언어',
  en: 'Language',
  th: 'ภาษา',
};
const languageOpen = ref(false);
const isActiveLanguage = code => locale.value === code;
const currentLanguageLabel = computed(
  () => languageItems.find(lang => isActiveLanguage(lang.code))?.label || ''
);
const languageGroupLabel = computed(
  () => languageGroupLabels[locale.value] || languageGroupLabels.en
);
const toggleLanguageMenu = () => {
  languageOpen.value = !languageOpen.value;
};
const switchLanguage = async code => {
  try {
    await updateUISettings({ locale: code });
    locale.value = code;
  } catch (error) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_ERROR')
    );
  }
};

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: toggleChatSupport,
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: 'https://www.chatwoot.com/hc/user-guide/en',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: 'i-lucide-scroll-text',
      link: 'https://www.chatwoot.com/changelog/',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
const logoutItem = computed(() =>
  allowedMenuItems.value.find(item => item.click === Auth.logout)
);
const itemsBeforeLogout = computed(() =>
  allowedMenuItems.value.filter(item => item !== logoutItem.value)
);
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="isCollapsed ? 'w-auto' : 'w-full'"
    @close="
      languageOpen = false;
      emit('close');
    "
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center p-1 text-left rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="[
          { 'bg-n-alpha-1': isOpen },
          isCollapsed ? 'justify-center' : 'w-full',
        ]"
        :title="isCollapsed ? currentUser.available_name : undefined"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
        />
        <div v-if="!isCollapsed" class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <DropdownBody class="bottom-12 z-50 mb-2 w-80 ltr:left-0 rtl:right-0">
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <template v-for="item in itemsBeforeLogout" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
      <CustomBrandPolicyWrapper :show-on-custom-branded-instance="true">
        <DropdownItem
          class="gap-3 hover:bg-n-alpha-2 rounded-lg"
          preserve-open
          :click="toggleLanguageMenu"
        >
          <Icon icon="i-lucide-languages" class="size-4 text-n-slate-11" />
          <span class="flex-1 truncate">{{ languageGroupLabel }}</span>
          <span class="truncate text-n-slate-11">{{
            currentLanguageLabel
          }}</span>
          <span
            class="size-4 text-n-slate-11"
            :class="
              languageOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
          />
        </DropdownItem>
        <template v-if="languageOpen">
          <DropdownItem
            v-for="lang in languageItems"
            :key="lang.code"
            :label="lang.label"
            :icon="isActiveLanguage(lang.code) ? 'i-lucide-check' : ''"
            :click="() => switchLanguage(lang.code)"
          />
        </template>
      </CustomBrandPolicyWrapper>
      <CustomBrandPolicyWrapper
        v-if="logoutItem"
        :show-on-custom-branded-instance="logoutItem.showOnCustomBrandedInstance"
      >
        <DropdownItem v-bind="logoutItem" />
      </CustomBrandPolicyWrapper>
    </DropdownBody>
  </DropdownContainer>
</template>
